import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import {
  canvasProposal,
  extractProposal,
  previewProposal,
  rerenderCanvas,
  saveProposal,
  type ExtractMode,
  type ExtractProvider,
  type ExtractResponse,
  type ProposalPayload,
} from '../api/proposals'
import { navigate } from '../App'
import { colors } from '../theme'
import ProposalFields from '../components/ProposalFields'
import SourcesPanel from '../components/SourcesPanel'
import CanvasEditor from './CanvasEditor'

type Stage = 'paste' | 'review' | 'canvas'

interface CanvasResult {
  /** Full HTML document — kept around so the Edit Layout view can drop
   *  the source into a sandboxed iframe for drag editing. */
  html: string
  pdfUrl: string
  summary?: string
  provider?: string
  model?: string
  calendlyEnriched: boolean
  resolvedProspectEmail?: string
  prospectEmailAutodetected?: boolean
}

// Bumped to v2 when we expanded from 2 providers → 3 model choices.
// v1 values ('claude' / 'gemini') still parse correctly: 'gemini' maps to
// the GA Gemini tier on first load. So does a stored 'gemini-2.5', the key
// that tier had before Google retired the 2.5 line (ELA 2026-10-20).
const MODEL_STORAGE_KEY = 'parkify_sales_portal_model_v2'

type ModelKey = 'claude' | 'gemini-3.8' | 'gemini-3.1'

interface ModelChoice {
  key: ModelKey
  label: string
  shortLabel: string
  provider: ExtractProvider
  model?: string   // omitted → backend uses env default
  tagline: string
}

// Order is the toggle order. Claude is the default for quality extraction;
// Gemini 3.8 Flash is the GA quality tier (3.6 Flash replaced 2.5 Pro 2026-09-24, 3.8 replaced 3.6 2026-10-06); Gemini 3.1 Pro Preview is the
// latest tier on Vertex AI for parkify-cs-prod (released 2026-02-19,
// confirmed via rawPredict probe in `location=global`).
const MODEL_CHOICES: ModelChoice[] = [
  {
    key: 'claude',
    label: 'Claude',
    shortLabel: 'CLAUDE',
    provider: 'claude',
    tagline: 'Opus 4.7 · Vertex AI · ADC',
  },
  {
    key: 'gemini-3.8',
    label: 'Gemini 3.8',
    shortLabel: 'GEMINI 3.8',
    provider: 'gemini',
    model: 'gemini-3.8-flash',
    tagline: '3.8 Flash · GA',
  },
  {
    key: 'gemini-3.1',
    label: 'Gemini 3.1',
    shortLabel: 'GEMINI 3.1',
    provider: 'gemini',
    // Pro Preview, not Flash-Lite — full Pro-tier reasoning. -customtools
    // sibling exists at `gemini-3.1-pro-preview-customtools` for agentic
    // workflows; we use the base endpoint for standard function-calling.
    model: 'gemini-3.1-pro-preview',
    tagline: '3.1 Pro Preview · latest',
  },
]

function loadStoredModelKey(): ModelKey {
  const v2 = localStorage.getItem(MODEL_STORAGE_KEY)
  if (v2 === 'claude' || v2 === 'gemini-3.8' || v2 === 'gemini-3.1') return v2
  // Retired picks keep the user on the GA Gemini tier.
  if (v2 === 'gemini-2.5' || v2 === 'gemini-3.6') return 'gemini-3.8'
  // v1 fallback — older `parkify_sales_portal_provider_v1` keys.
  const v1 = localStorage.getItem('parkify_sales_portal_provider_v1')
  if (v1 === 'gemini') return 'gemini-3.8'
  return 'claude'
}

const SAMPLE_NOTES = `# Demo recap — Nathan / 364 Thames St., Newport RI
Spoke with Nathan today. Operates ~40 surface spots behind the building.
Currently using Flowbird meters that are 12 yrs old. Plates cars manually.
Frustrated with cash handling + no analytics. Asked about LPR — interested
but doesn't want hardware purchase up front. Said budget is tight, would
need to do the surface-lot only.

Next step: send proposal, schedule reference call with a portfolio op.
Rep: Drake Vahey · dvahey@parkify.com`

export default function NewProposal() {
  const { authHeaders, apiBase, user } = useAuth()
  const [stage, setStage] = useState<Stage>('paste')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [extracted, setExtracted] = useState<ExtractResponse | null>(null)
  const [payload, setPayload] = useState<ProposalPayload | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [modelKey, setModelKey] = useState<ModelKey>(() => loadStoredModelKey())
  const [prospectEmail, setProspectEmail] = useState('')
  const [mode, setMode] = useState<ExtractMode>(() => {
    const v = localStorage.getItem('parkify_sales_portal_mode_v1')
    if (v === 'draft' || v === 'surprise' || v === 'canvas') return v
    return 'extract'
  })
  const [canvasResult, setCanvasResult] = useState<CanvasResult | null>(null)
  // True while the drag-to-nudge editor overlay is open. We keep the
  // editor mounted only when the rep explicitly opens it because the
  // iframe boot is non-trivial (parses + renders the full HTML doc).
  const [canvasEditing, setCanvasEditing] = useState(false)
  // True while we're calling /canvas/render after a manual edit. Distinct
  // from `busy` (which gates the paste-stage Compose button).
  const [canvasRerendering, setCanvasRerendering] = useState(false)

  // Clean up object URLs we own when they change or the screen unmounts —
  // each createObjectURL leaks until revoked.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  // Same cleanup for the Canvas-mode PDF blob URL.
  useEffect(() => {
    return () => {
      if (canvasResult?.pdfUrl) URL.revokeObjectURL(canvasResult.pdfUrl)
    }
  }, [canvasResult])

  // Editing fields invalidates the preview — drop it so the rep doesn't
  // think a stale render is current.
  useEffect(() => {
    if (!payload) return
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    // intentionally only watch payload identity — the freshness flag is
    // "render again to refresh."
  }, [payload])

  async function onExtract() {
    setBusy(true)
    setError(null)
    const choice = MODEL_CHOICES.find((c) => c.key === modelKey) ?? MODEL_CHOICES[0]
    try {
      if (mode === 'canvas') {
        // Canvas mode returns a PDF blob directly — no review-fields stage.
        // Drop any stale canvas preview before requesting a new one.
        if (canvasResult?.pdfUrl) URL.revokeObjectURL(canvasResult.pdfUrl)
        setCanvasResult(null)
        const r = await canvasProposal(
          apiBase,
          authHeaders(),
          notes,
          user?.name ?? '',
          user?.email ?? '',
          choice.provider,
          choice.model,
          prospectEmail.trim() || undefined,
        )
        const pdfUrl = URL.createObjectURL(r.pdf)
        setCanvasResult({
          html: r.html,
          pdfUrl,
          summary: r.summary,
          provider: r.provider,
          model: r.model,
          calendlyEnriched: r.calendly_enriched,
          resolvedProspectEmail: r.resolved_prospect_email,
          prospectEmailAutodetected: r.prospect_email_autodetected,
        })
        setStage('canvas')
        return
      }
      const r = await extractProposal(
        apiBase,
        authHeaders(),
        notes,
        user?.name ?? '',
        user?.email ?? '',
        choice.provider,
        choice.model,
        prospectEmail.trim() || undefined,
        mode,
      )
      setExtracted(r)
      setPayload(r.payload)
      setStage('review')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Extraction failed')
    } finally {
      setBusy(false)
    }
  }

  function pickModel(next: ModelKey) {
    setModelKey(next)
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, next)
    } catch {
      /* localStorage disabled — toggle still works in-memory */
    }
  }

  function pickMode(next: ExtractMode) {
    setMode(next)
    try {
      localStorage.setItem('parkify_sales_portal_mode_v1', next)
    } catch {
      /* in-memory only */
    }
  }

  // Called when the rep clicks "Apply & Re-render PDF" in the canvas
  // editor. We ship the modified HTML back to the server, get a fresh
  // PDF, and swap the iframe blob URL. The HTML in `canvasResult` is
  // updated so subsequent edits start from the new baseline.
  async function onApplyEditedHtml(modifiedHtml: string) {
    if (!canvasResult) return
    setCanvasRerendering(true)
    setError(null)
    try {
      const pdf = await rerenderCanvas(apiBase, authHeaders(), modifiedHtml)
      const newUrl = URL.createObjectURL(pdf)
      // Release the previous blob before we overwrite the state entry.
      URL.revokeObjectURL(canvasResult.pdfUrl)
      setCanvasResult({
        ...canvasResult,
        html: modifiedHtml,
        pdfUrl: newUrl,
      })
      setCanvasEditing(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Re-render failed')
    } finally {
      setCanvasRerendering(false)
    }
  }

  async function onPreview() {
    if (!payload) return
    setPreviewing(true)
    setError(null)
    try {
      const blob = await previewProposal(apiBase, authHeaders(), payload)
      const url = URL.createObjectURL(blob)
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return url
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed')
    } finally {
      setPreviewing(false)
    }
  }

  async function onSave() {
    if (!payload) return
    setBusy(true)
    setError(null)
    try {
      const saved = await saveProposal(apiBase, authHeaders(), payload, notes)
      navigate({ name: 'detail', id: saved.id })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <button
        onClick={() => navigate({ name: 'proposals' })}
        style={{
          background: 'transparent',
          border: 'none',
          color: colors.purple,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          padding: 0,
          marginBottom: 16,
          cursor: 'pointer',
        }}
      >
        ← Back to proposals
      </button>

      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2.5px',
          color: colors.purple,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        Build a proposal
      </div>
      <h1
        style={{
          fontSize: 32,
          fontWeight: 700,
          letterSpacing: '-1px',
          margin: '0 0 8px',
          color: colors.black,
        }}
      >
        New proposal
      </h1>
      <p
        style={{
          fontSize: 14,
          color: colors.textMuted,
          margin: '0 0 28px',
          maxWidth: 620,
          lineHeight: 1.6,
        }}
      >
        Paste your call notes, email recap, or Calendly invite. We pull
        out the essentials, you preview the PDF, then generate when it
        looks right.
      </p>

      <Stepper stage={stage} />

      {stage === 'paste' && (
        <Panel>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 14,
              flexWrap: 'wrap',
              marginBottom: 12,
            }}
          >
            <SectionLabel>Call notes / demo recap</SectionLabel>
            <ModelToggle value={modelKey} onChange={pickModel} />
          </div>
          <SourcesPanel
            onInsert={(text) =>
              setNotes((cur) => (cur ? cur + text : text.trimStart()))
            }
          />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={SAMPLE_NOTES}
            rows={14}
            style={{
              width: '100%',
              padding: '14px 16px',
              background: colors.bgAlt,
              color: colors.black,
              border: `1px solid ${colors.divider}`,
              borderRadius: 10,
              fontSize: 14,
              lineHeight: 1.55,
              fontFamily:
                "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Monaco, monospace",
              marginTop: 12,
            }}
          />

          {/* Prospect email — optional override for the Calendly enrichment
              hook. Left blank, the backend auto-detects an email from the
              call notes (first non-Parkify address that isn't the rep's
              own). When set, the backend looks the email up in Calendly
              and folds demo intake Q&A into the prompt context. Silent
              no-op when no Calendly cache hit. */}
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                color: colors.purple,
                marginBottom: 6,
              }}
            >
              Prospect email · auto-detected from notes
            </div>
            <input
              type="email"
              value={prospectEmail}
              onChange={(e) => setProspectEmail(e.target.value)}
              placeholder="Leave blank to auto-detect — or type to override"
              autoComplete="off"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: colors.white,
                color: colors.black,
                border: `1px solid ${colors.divider}`,
                borderRadius: 8,
                fontSize: 13,
                outline: 'none',
              }}
            />
          </div>

          {/* AI mode toggle — Extract (cover fields only, defaults elsewhere)
              vs Draft (LLM fills in problem cards, solution features, timeline,
              etc. tailored to the notes). */}
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                color: colors.purple,
                marginBottom: 6,
              }}
            >
              AI mode
            </div>
            <ModeToggle value={mode} onChange={pickMode} />
          </div>

          {error ? <InlineError>{error}</InlineError> : null}

          <ActionRow>
            <SecondaryBtn
              onClick={() => {
                setNotes('')
                setProspectEmail('')
              }}
              disabled={!notes && !prospectEmail}
            >
              Clear
            </SecondaryBtn>
            <PrimaryBtn onClick={onExtract} disabled={busy || !notes.trim()}>
              {busy
                ? mode === 'canvas'
                  ? 'Composing…'
                  : mode === 'surprise'
                    ? 'Surprising…'
                    : mode === 'draft'
                      ? 'Drafting…'
                      : 'Extracting…'
                : mode === 'canvas'
                  ? 'Compose canvas →'
                  : mode === 'surprise'
                    ? 'Surprise me →'
                    : mode === 'draft'
                      ? 'AI-draft full proposal →'
                      : 'Extract proposal fields →'}
            </PrimaryBtn>
          </ActionRow>
        </Panel>
      )}

      {stage === 'review' && payload && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {extracted?.ai_surprise ? (
              <div
                style={{
                  padding: '8px 14px',
                  background: colors.orange,
                  border: `1px solid ${colors.orange}`,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: colors.white,
                }}
              >
                <span aria-hidden>★</span> Surprise me — creative draft
              </div>
            ) : extracted?.ai_drafted ? (
              <div
                style={{
                  padding: '8px 14px',
                  background: colors.purple,
                  border: `1px solid ${colors.purple}`,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: colors.white,
                }}
              >
                <span aria-hidden>✨</span> AI-drafted full proposal
              </div>
            ) : null}
            {extracted?.calendly_enriched ? (
              <div
                style={{
                  padding: '8px 14px',
                  background: colors.purpleSoft,
                  border: `1px solid ${colors.purple}`,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: colors.purple,
                }}
              >
                <span aria-hidden>✓</span> Enriched with Calendly demo Q&A
              </div>
            ) : null}
            {extracted?.prospect_email_autodetected && extracted?.resolved_prospect_email ? (
              <div
                style={{
                  padding: '8px 14px',
                  background: colors.white,
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  fontFamily: 'ui-monospace, monospace',
                }}
              >
                <span aria-hidden style={{ color: colors.purple, fontWeight: 700 }}>↳</span>
                Found in notes: {extracted.resolved_prospect_email}
              </div>
            ) : null}
          </div>
          {extracted && extracted.warnings.length > 0 ? (
            <div
              style={{
                marginBottom: 18,
                padding: '14px 18px',
                background: colors.warningBg,
                border: `1.5px solid ${colors.warningBorder}`,
                borderRadius: 10,
                fontSize: 13,
                lineHeight: 1.55,
                color: colors.black,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: '2.5px',
                  fontWeight: 700,
                  color: colors.red,
                  marginBottom: 8,
                  textTransform: 'uppercase',
                }}
              >
                Check before sending
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {extracted.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <ProposalFields
            value={payload}
            onChange={setPayload}
            confidence={extracted?.confidence}
          />

          {error ? <InlineError>{error}</InlineError> : null}

          <ActionRow split>
            <SecondaryBtn onClick={() => setStage('paste')}>
              ← Edit notes
            </SecondaryBtn>
            <div style={{ display: 'flex', gap: 10 }}>
              <SecondaryBtn onClick={onPreview} disabled={previewing}>
                {previewing
                  ? 'Rendering…'
                  : previewUrl
                    ? 'Refresh preview'
                    : 'Preview PDF'}
              </SecondaryBtn>
              <PrimaryBtn onClick={onSave} disabled={busy}>
                {busy ? 'Generating…' : 'Generate & save'}
              </PrimaryBtn>
            </div>
          </ActionRow>

          <PreviewPane url={previewUrl} loading={previewing} />
        </div>
      )}

      {stage === 'canvas' && canvasResult && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <div
              style={{
                padding: '8px 14px',
                background: colors.purple,
                border: `1px solid ${colors.purple}`,
                borderRadius: 999,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: colors.white,
              }}
            >
              <span aria-hidden>◆</span> Canvas mode · freeform draft
            </div>
            {canvasResult.calendlyEnriched ? (
              <div
                style={{
                  padding: '8px 14px',
                  background: colors.purpleSoft,
                  border: `1px solid ${colors.purple}`,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: colors.purple,
                }}
              >
                <span aria-hidden>✓</span> Enriched with Calendly demo Q&A
              </div>
            ) : null}
            {canvasResult.prospectEmailAutodetected && canvasResult.resolvedProspectEmail ? (
              <div
                style={{
                  padding: '8px 14px',
                  background: colors.white,
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  fontFamily: 'ui-monospace, monospace',
                }}
              >
                <span aria-hidden style={{ color: colors.purple, fontWeight: 700 }}>↳</span>
                Found in notes: {canvasResult.resolvedProspectEmail}
              </div>
            ) : null}
          </div>

          {canvasResult.summary ? (
            <div
              style={{
                marginBottom: 16,
                padding: '14px 18px',
                background: colors.white,
                border: `1px solid ${colors.divider}`,
                borderRadius: 12,
                fontSize: 13,
                lineHeight: 1.55,
                color: colors.black,
                boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: '2.5px',
                  fontWeight: 700,
                  color: colors.purple,
                  marginBottom: 6,
                  textTransform: 'uppercase',
                }}
              >
                Creative direction
              </div>
              {canvasResult.summary}
            </div>
          ) : null}

          {error ? <InlineError>{error}</InlineError> : null}

          <ActionRow split>
            <SecondaryBtn onClick={() => setStage('paste')}>
              ← Edit notes
            </SecondaryBtn>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <SecondaryBtn
                onClick={() => {
                  // Re-trigger canvas generation with the same inputs — the
                  // LLM's temperature is high so each run produces a different
                  // creative direction.
                  void onExtract()
                }}
                disabled={busy || canvasRerendering}
              >
                {busy ? 'Composing…' : 'Regenerate'}
              </SecondaryBtn>
              <button
                type="button"
                onClick={() => setCanvasEditing(true)}
                disabled={canvasRerendering}
                style={{
                  background: colors.white,
                  color: colors.purple,
                  border: `1px solid ${colors.purple}`,
                  borderRadius: 8,
                  padding: '10px 18px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  cursor: canvasRerendering ? 'not-allowed' : 'pointer',
                  opacity: canvasRerendering ? 0.6 : 1,
                }}
              >
                {canvasRerendering ? 'Re-rendering…' : '✥ Edit Layout'}
              </button>
              <a
                href={canvasResult.pdfUrl}
                download="parkify-canvas.pdf"
                style={{
                  background: colors.purple,
                  color: colors.white,
                  border: 'none',
                  borderRadius: 8,
                  padding: '11px 22px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                Download PDF
              </a>
            </div>
          </ActionRow>

          <CanvasPreviewPane
            url={canvasResult.pdfUrl}
            provider={canvasResult.provider}
            model={canvasResult.model}
          />

          {canvasEditing ? (
            <CanvasEditor
              html={canvasResult.html}
              onSave={(modified) => {
                void onApplyEditedHtml(modified)
              }}
              onCancel={() => setCanvasEditing(false)}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

function CanvasPreviewPane({
  url,
  provider,
  model,
}: {
  url: string
  provider?: string
  model?: string
}) {
  return (
    <div
      style={{
        marginTop: 18,
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: `1px solid ${colors.divider}`,
          background: colors.bgAlt,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: colors.purple,
          }}
        >
          Canvas PDF preview
          {provider || model ? (
            <span
              style={{
                marginLeft: 8,
                color: colors.textMuted,
                letterSpacing: '1.2px',
                fontWeight: 400,
              }}
            >
              · {provider}
              {model ? ` · ${model}` : ''}
            </span>
          ) : null}
        </div>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: colors.purple,
          }}
        >
          Open in new tab ↗
        </a>
      </div>
      <iframe
        title="Canvas-mode proposal PDF"
        src={url}
        style={{
          width: '100%',
          height: 760,
          border: 'none',
          background: colors.gray,
          display: 'block',
        }}
      />
    </div>
  )
}

// ── PDF preview ──────────────────────────────────────────────────────────────

function PreviewPane({ url, loading }: { url: string | null; loading: boolean }) {
  if (!url && !loading) return null
  return (
    <div
      style={{
        marginTop: 18,
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: `1px solid ${colors.divider}`,
          background: colors.bgAlt,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
            color: colors.purple,
          }}
        >
          PDF preview · not yet saved
        </div>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: colors.purple,
            }}
          >
            Open in new tab ↗
          </a>
        ) : null}
      </div>
      {url ? (
        <iframe
          title="Proposal preview"
          src={url}
          // 10×7.5" widescreen renderer → ~4:3 page. Tall enough to show
          // the cover + 2nd page without scrolling on a typical monitor.
          style={{
            width: '100%',
            height: 760,
            border: 'none',
            background: colors.gray,
            display: 'block',
          }}
        />
      ) : (
        <div
          style={{
            padding: '60px 20px',
            textAlign: 'center',
            color: colors.textMuted,
            fontSize: 13,
          }}
        >
          Rendering preview…
        </div>
      )}
    </div>
  )
}

// ── Model toggle (Claude / Gemini 2.5 / Gemini 3.1) ──────────────────────────

function ModelToggle({
  value,
  onChange,
}: {
  value: ModelKey
  onChange: (next: ModelKey) => void
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Extraction model"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 4,
        background: colors.bgAlt,
        border: `1px solid ${colors.divider}`,
        borderRadius: 999,
      }}
    >
      {MODEL_CHOICES.map((choice) => (
        <ModelChip
          key={choice.key}
          active={value === choice.key}
          label={choice.shortLabel}
          title={choice.tagline}
          onClick={() => onChange(choice.key)}
        />
      ))}
    </div>
  )
}

function ModelChip({
  active,
  label,
  title,
  onClick,
}: {
  active: boolean
  label: string
  title: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      title={title}
      style={{
        background: active ? colors.purple : 'transparent',
        color: active ? colors.white : colors.black,
        border: 'none',
        borderRadius: 999,
        padding: '6px 12px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.1px',
        textTransform: 'uppercase',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  )
}

// ── Mode toggle (Extract / Draft) ────────────────────────────────────────────

function ModeToggle({
  value,
  onChange,
}: {
  value: ExtractMode
  onChange: (next: ExtractMode) => void
}) {
  return (
    <div
      role="radiogroup"
      aria-label="AI mode"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 4,
        background: colors.bgAlt,
        border: `1px solid ${colors.divider}`,
        borderRadius: 999,
      }}
    >
      <ModeChip
        active={value === 'extract'}
        label="EXTRACT"
        title="Only the cover identity fields — defaults for everything else"
        onClick={() => onChange('extract')}
      />
      <ModeChip
        active={value === 'draft'}
        label="DRAFT"
        title="AI fills in the whole proposal tailored to your notes (templated voice)"
        onClick={() => onChange('draft')}
      />
      <ModeChip
        active={value === 'surprise'}
        label="SURPRISE"
        title="AI takes bolder creative liberties — punchier copy, vivid framing, prospect-specific point of view. Same brand colors and fonts; same page structure."
        onClick={() => onChange('surprise')}
      />
      <ModeChip
        active={value === 'canvas'}
        label="CANVAS"
        title="No structural restraints — AI generates a complete freeform HTML proposal with custom layouts, page count, inline SVG illustrations and gradients. Brand colors, fonts, and logo locked. Experimental."
        onClick={() => onChange('canvas')}
      />
    </div>
  )
}

function ModeChip({
  active,
  label,
  title,
  onClick,
}: {
  active: boolean
  label: string
  title: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      title={title}
      style={{
        background: active ? colors.purple : 'transparent',
        color: active ? colors.white : colors.black,
        border: 'none',
        borderRadius: 999,
        padding: '6px 14px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.1px',
        textTransform: 'uppercase',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}

// ── Local UI helpers ─────────────────────────────────────────────────────────

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        padding: 24,
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
      }}
    >
      {children}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '2.5px',
        textTransform: 'uppercase',
        color: colors.purple,
      }}
    >
      {children}
    </div>
  )
}

function ActionRow({
  children,
  split,
}: {
  children: React.ReactNode
  split?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        justifyContent: split ? 'space-between' : 'flex-end',
        marginTop: 20,
      }}
    >
      {children}
    </div>
  )
}

function PrimaryBtn({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: colors.purple,
        color: colors.white,
        border: 'none',
        borderRadius: 8,
        padding: '11px 22px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  )
}

function SecondaryBtn({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: colors.white,
        color: colors.black,
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        padding: '10px 18px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  )
}

function InlineError({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 14,
        padding: '12px 14px',
        background: colors.warningBg,
        border: `1.5px solid ${colors.warningBorder}`,
        borderRadius: 10,
        fontSize: 13,
        color: colors.black,
      }}
    >
      <span style={{ color: colors.red, fontWeight: 700, marginRight: 6 }}>⚠</span>
      {children}
    </div>
  )
}

function Stepper({ stage }: { stage: Stage }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        marginBottom: 20,
        fontSize: 11,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <StepperPill active={stage === 'paste'} num={1} label="Paste notes" />
      <Dot />
      <StepperPill active={stage === 'review'} num={2} label="Review & preview" />
      <Dot />
      <StepperPill active={false} num={3} label="Generate & save" />
    </div>
  )
}

function Dot() {
  return <span style={{ color: colors.gray }}>·</span>
}

function StepperPill({
  active,
  num,
  label,
}: {
  active: boolean
  num: number
  label: string
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 999,
        background: active ? colors.purple : colors.white,
        border: `1px solid ${active ? colors.purple : colors.divider}`,
        color: active ? colors.white : colors.textMuted,
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: '1px',
        textTransform: 'uppercase',
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: active ? colors.white : colors.bg,
          color: active ? colors.purple : colors.textMuted,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
        }}
      >
        {num}
      </span>
      {label}
    </span>
  )
}
